import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDateShort } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';
import { useActionStreamStore } from '../../stores/actionStream.store';
import { useNotifications } from '../../hooks/useNotifications';
import {
  computeUpcoming, toUnifiedRows, invoicesQueryOptions, quotesQueryOptions,
  type QuoteItem, type UnifiedRow,
} from '../../lib/scadenziario';
import type { IconName } from '../../components/ui/Icon';
import { tasksSummaryQuery, daysToDue, contextLine, type TaskSummaryItem } from '../tasks/tasksApi';
import {
  adminOverviewQuery, upcomingRateQuery, daysFromToday,
  type IncassoRow, type TaxNext, type ToReview,
} from '../admin/adminApi';
import {
  lotto4, scadenzaInParole, tempoInParole,
  type AbbinamentoSicuroUscita, type CanoneDaFatturare, type CifraMancante, type StatoSync, type VersamentiDaVerificare,
} from '../admin/situazione/situazioneApi';
import { fmtDate } from '../../lib/utils';
import { ADMIN_PATH, adminHref, adminLinks } from '../../lib/adminRoutes';
import { ModuleKey, PermissionLevel, TAX_SOURCE_LABEL, TAX_SOURCES_PROVVISORIE, hasAtLeast } from '@masamune/shared';

/**
 * useActionStream — composer dello stream "Oggi".
 *
 * Aggrega in un'unica lista azionabile le fonti già esistenti (cache TanStack
 * condivisa, nessun fetch nuovo):
 *   · ['invoices'] + ['quotes']        → scadenziario (incassi e firme)
 *   · ['notifications']                → alert di progetto + FYI personali
 *   · ['dashboard', q, y]              → contatori aggregati (bozze, candidati)
 *   · ['tasks','summary','mine']       → i miei task in ritardo e in scadenza oggi
 *   · ['admin','overview'] + rate      → Amministrazione: rate scadute / in scadenza,
 *                                        scadenze fiscali entro 7 giorni, "da controllare"
 *
 * `scope: 'admin'` (Situazione › "Cose da fare oggi"): SOLO le voci
 * dell'Amministrazione, piu' tre tipi che nella home non servono — la
 * scadenza fiscale senza cifra (entro 30 giorni), l'aggiornamento di Qonto o
 * FattureInCloud che non e' riuscito (con "Riprova") e gli stipendi e TFR
 * senza bonifico da verificare in Dipendenti. Stessa fonte, stesse chiavi e
 * stesso store: una cosa fatta o posticipata da una parte sparisce anche
 * dall'altra.
 *
 * In tutte e due (home e Situazione): il bonifico che paga con certezza la
 * fattura di un fornitore (da `fatture.toReview`, dal 24/9 anche le uscite) e
 * il canone scaduto senza la fattura del mese ("Fattura il canone di ...").
 *
 * Bande di urgenza:
 *   0 = Critico (rate/fatture scadute, tasse entro 7 gg, alert rischio/margine, task in ritardo)
 *   1 = Oggi    (scadenze entro domani, da controllare, preventivi in scadenza, task che scadono oggi)
 *   2 = Settimana (scadenze ≤ 7gg, preventivi in attesa, item aggregati)
 *   3 = FYI     (notifiche personali non lette)
 *
 * Con il modulo AMMINISTRAZIONE le rate (fonte FiC, anche multiple per fattura)
 * sostituiscono le fatture dello scadenziario di Contabilità: stessa cosa,
 * più precisa. Senza il modulo resta il comportamento di prima.
 *
 * Hook puro-composer: NESSUNA mutation viene eseguita qui — `primary` e
 * `actionData` descrivono l'azione, il componente riga la collega.
 */

export type ActionBand = 0 | 1 | 2 | 3;
export type ActionKind =
  | 'invoice-overdue' | 'invoice-due' | 'quote-pending'
  | 'project-alert' | 'candidate-ready' | 'draft-project' | 'notification'
  | 'task-overdue' | 'task-due'
  | 'rata-overdue' | 'rata-due' | 'tax-due' | 'to-review'
  | 'bank-sure' | 'tax-missing' | 'sync-failed' | 'canone-da-fatturare' | 'versamenti-da-verificare';
export type ActionPrimaryKind =
  | 'sollecita' | 'incassa' | 'reinvia' | 'risolvi' | 'apri' | 'copia-link' | 'completa'
  | 'incassa-rata' | 'conferma-incasso' | 'riprova';
// 'notification' è il fallback per le notifiche con link non riconducibile a un'entità
export type ActionEntityType = 'invoice' | 'quote' | 'project' | 'client' | 'candidate' | 'notification' | 'task' | 'rata' | 'tax' | 'payable-rata' | 'sync' | 'canone' | 'dipendenti';

/** Le voci che sono lavoro dell'Amministrazione: quelle che la Situazione mostra. */
export const ADMIN_KINDS: ReadonlySet<ActionKind> = new Set<ActionKind>([
  'rata-overdue', 'rata-due', 'tax-due', 'to-review', 'bank-sure', 'tax-missing', 'sync-failed',
  'canone-da-fatturare', 'versamenti-da-verificare',
]);

export interface ActionItem {
  /** `entityType:id` — chiave stabile per snooze / archivio / dedupe. */
  key: string;
  band: ActionBand;
  kind: ActionKind;
  icon: IconName;
  title: string;
  sub: string;
  amount?: number;
  dueAt?: string | null;
  entity: { type: ActionEntityType; id: string; path: string };
  primary?: { label: string; kind: ActionPrimaryKind };
  canSnooze: boolean;
  canDone: boolean;
  /** Payload minimo per il wiring delle mutation nel componente riga (fase UI). */
  actionData?: {
    clientId?: string;
    number?: string | null;
    title?: string | null;
    alertId?: string;
    notifId?: string;
    /** Stato del task prima di "Completa" (per l'Annulla del toast). */
    taskStatus?: string;
    /** Rata (Amministrazione): id del pagamento e, se c'è, il movimento Qonto compatibile. */
    paymentId?: string;
    transactionId?: string;
    settledAt?: string;
    /** Aggiornamento fallito: quale dei due rilanciare con "Riprova". */
    provider?: 'QONTO' | 'FIC';
  };
}

export interface ActionStreamCounts {
  critical: number;
  today: number;
  week: number;
  later: number;
  snoozed: number;
}

export interface ActionBandGroup { band: ActionBand; label: string; items: ActionItem[] }

/**
 * Ogni etichetta nomina la cosa (SPEC amministrazione-v2, regola etichette):
 * mai "Critico" da solo, e nemmeno "Da fare subito" senza il soggetto.
 */
export const BAND_LABEL: Record<ActionBand, string> = {
  0: 'Cose da fare subito',
  1: 'Cose da fare oggi',
  2: 'Cose da fare in settimana',
  3: 'Avvisi da leggere',
};

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
/** "settembre", oppure "dicembre 2025" se l'anno non è quello di oggi. */
function meseInParole(key: string): string {
  const nome = MESI[Number(key.slice(5, 7)) - 1] ?? key;
  return Number(key.slice(0, 4)) === new Date().getFullYear() ? nome : `${nome} ${key.slice(0, 4)}`;
}
/** "a, b e c". */
function elenco(xs: string[]): string {
  return xs.length <= 1 ? xs[0] ?? '' : `${xs.slice(0, -1).join(', ')} e ${xs[xs.length - 1]}`;
}

const DAY_MS = 86400000;

// Sottoinsieme di GET /dashboard/overview: allo stream servono solo i contatori.
interface OverviewLite { kpis: { draftProjects: number; pipelineCandidates: number } }

// Icona notifica server → IconName (stessa semantica dell'ICON_MAP in Topbar).
const NOTIF_ICON: Record<string, IconName> = {
  INVOICE: 'invoice', CALENDAR: 'calendar', TALENT: 'talent', FILE: 'file', ALERT: 'alert', OTHER: 'bell',
};

// Deriva l'entità da un link di notifica noto: così una notifica sulla
// fattura X si deduplica con l'item fattura X (stessa chiave entità).
const LINK_PATTERNS: Array<{ re: RegExp; type: ActionEntityType }> = [
  { re: /^\/tasks\/([^/?#]+)/, type: 'task' },
  { re: /^\/invoices\/([^/?#]+)/, type: 'invoice' },
  { re: /^\/quotes\/([^/?#]+)/, type: 'quote' },
  { re: /^\/projects\/([^/?#]+)/, type: 'project' },
  { re: /^\/clients\/([^/?#]+)/, type: 'client' },
];
function entityFromLink(link: string | null): ActionItem['entity'] | null {
  if (!link) return null;
  for (const p of LINK_PATTERNS) {
    const m = p.re.exec(link);
    if (m?.[1]) return { type: p.type, id: m[1], path: link };
  }
  return null;
}

/** Fattura in ritardo → band 0. Copre sia EMESSA/INVIATA oltre scadenza sia status SCADUTA. */
function invoiceOverdueItem(r: UnifiedRow, due: number | null, now: number): ActionItem {
  // Stessa convenzione dello Scadenziario: days = ceil((due - now) / giorno), ritardo = -days
  const late = due != null ? -Math.ceil((due - now) / DAY_MS) : null;
  return {
    key: `invoice:${r.id}`,
    band: 0,
    kind: 'invoice-overdue',
    icon: 'alert',
    title: `Fattura ${r.number ?? 's/n'} · ${r.client.legalName}`,
    // Il tempo in parole ("in ritardo da 2 settimane"), la data esatta nel tooltip della riga.
    sub: late == null ? 'In ritardo, da sollecitare' : late <= 0 ? 'Scaduta oggi' : `In ritardo ${tempoInParole(-late)}`,
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'invoice', id: r.id, path: `/invoices/${r.id}` },
    primary: { label: 'Sollecita', kind: 'sollecita' },
    canSnooze: true, // band 0: lo store clampa lo snooze a domani 09:00
    canDone: true,   // done = Incassa (chiude l'item)
    actionData: { clientId: r.client.id, number: r.number },
  };
}

/** Fattura aperta in scadenza → band 1 (entro domani) o 2 (entro 7 giorni). */
function invoiceDueItem(r: UnifiedRow, due: number, now: number, band: 1 | 2): ActionItem {
  const days = Math.ceil((due - now) / DAY_MS);
  return {
    key: `invoice:${r.id}`,
    band,
    kind: 'invoice-due',
    icon: 'invoice',
    title: `Fattura ${r.number ?? 's/n'} · ${r.client.legalName}`,
    sub: scadenzaInParole(Math.max(0, days)),
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'invoice', id: r.id, path: `/invoices/${r.id}` },
    primary: { label: 'Segna incassata', kind: 'incassa' },
    canSnooze: true,
    canDone: true,
    actionData: { clientId: r.client.id, number: r.number },
  };
}

/** Preventivo INVIATO/VISUALIZZATO in attesa di firma → band 1 se la scadenza è vicina, altrimenti 2. */
function quotePendingItem(r: UnifiedRow, band: 1 | 2, due: number | null, now: number): ActionItem {
  const quote = r.raw as QuoteItem;
  const days = due != null ? Math.ceil((due - now) / DAY_MS) : null;
  const expiry = days == null ? null
    : days < 0 ? `validità superata ${tempoInParole(days)}`
    : days === 0 ? 'scade oggi'
    : `scade ${tempoInParole(days)}`;
  return {
    key: `quote:${r.id}`,
    band,
    kind: 'quote-pending',
    icon: 'send',
    title: `Preventivo ${r.number ?? 's/n'} · ${r.client.legalName}`,
    sub: `${r.status === 'VISUALIZZATO' ? 'Visto dal cliente' : 'Inviato'}, in attesa di firma${expiry ? ` · ${expiry}` : ''}`,
    amount: Number(r.total),
    dueAt: r.secondaryDate,
    entity: { type: 'quote', id: r.id, path: `/quotes/${r.id}` },
    primary: { label: 'Reinvia', kind: 'reinvia' },
    canSnooze: true,
    canDone: false, // la firma dipende dal cliente: non si "chiude" da qui
    actionData: { clientId: r.client.id, number: r.number, title: quote.title },
  };
}

/** Task mio in ritardo (band 0) o che scade oggi (band 1): primary = Completa. */
function taskActionItem(t: TaskSummaryItem, band: 0 | 1): ActionItem {
  const days = daysToDue(t.dueDate);
  const ctx = contextLine(t);
  const when = band === 0
    ? (days != null && days < 0 ? `In ritardo ${tempoInParole(days)}` : 'In ritardo')
    : 'Scade oggi';
  return {
    key: `task:${t.id}`,
    band,
    kind: band === 0 ? 'task-overdue' : 'task-due',
    icon: 'tasks',
    title: t.title,
    sub: ctx ? `${when} · ${ctx}` : when,
    dueAt: t.dueDate,
    entity: { type: 'task', id: t.id, path: `/tasks/${t.id}` },
    primary: { label: 'Segna completato', kind: 'completa' },
    canSnooze: true,
    canDone: true, // done = Completa (chiude il task)
    actionData: { title: t.title, taskStatus: t.status },
  };
}

/**
 * Rata di una fattura emessa (Amministrazione). Scaduta → band 0 (Incassa);
 * entro domani → 1; entro 7 giorni → 2. Se esiste un bonifico Qonto
 * compatibile, la primary diventa "Conferma" con quel movimento.
 */
function rataItem(r: IncassoRow, band: 0 | 1 | 2, review: ToReview | undefined): ActionItem {
  const days = daysFromToday(r.dueDate);
  const rata = r.ofTotal > 1 ? ` · rata ${r.seq} di ${r.ofTotal}` : '';
  // Il tempo in parole ("in ritardo da 6 mesi"); la data esatta la dà il tooltip della riga.
  const when = scadenzaInParole(days);
  const hint = review ? ` · bonifico del ${fmtDate(review.transaction.settledOn)} in banca` : '';
  return {
    key: `rata:${r.id}`,
    band,
    kind: band === 0 ? 'rata-overdue' : 'rata-due',
    icon: band === 0 ? 'alert' : 'coins',
    title: `Fattura ${r.invoiceNumber} · ${r.client.shortName}`,
    sub: `${when}${rata}${hint}`,
    amount: r.amount,
    dueAt: r.dueDate,
    entity: { type: 'rata', id: r.id, path: adminLinks.fattura(r.invoiceNumber) },
    primary: review ? { label: "Conferma l'incasso", kind: 'conferma-incasso' } : { label: 'Segna incassata', kind: 'incassa-rata' },
    canSnooze: true,
    canDone: true, // done = Incassa (chiude la rata)
    actionData: { paymentId: r.id, number: r.invoiceNumber, transactionId: review?.transaction.id, settledAt: review?.transaction.settledOn },
  };
}

/** Rata con un movimento Qonto compatibile ma non in scadenza a breve → Oggi, "Conferma". */
function toReviewItem(t: ToReview): ActionItem {
  return {
    key: `rata:${t.paymentId}`,
    band: 1,
    kind: 'to-review',
    icon: 'swap',
    title: `Fattura ${t.invoiceNumber} · ${t.clientName}`,
    sub: `Bonifico del ${fmtDate(t.transaction.settledOn)} in banca: è questo incasso?`,
    amount: t.amount,
    dueAt: t.dueDate,
    entity: { type: 'rata', id: t.paymentId, path: adminLinks.fattura(t.invoiceNumber) },
    primary: { label: "Conferma l'incasso", kind: 'conferma-incasso' },
    canSnooze: true,
    canDone: true,
    actionData: { paymentId: t.paymentId, number: t.invoiceNumber, transactionId: t.transaction.id, settledAt: t.transaction.settledOn },
  };
}

/**
 * Scadenza fiscale entro 7 giorni (o già passata) → Critico. Si apre, non si "chiude" da qui.
 *
 * Se a sistema la cifra è zero (l'imposta di registro del 30/9, per esempio)
 * la cosa da fare non è pagare ma scrivere quanto: la riga lo dice, e non
 * mostra un "€ 0,00" che si leggerebbe come "non c'è niente da pagare".
 */
function taxItem(t: TaxNext, idx: number): ActionItem {
  const days = daysFromToday(t.dueDate);
  const when = days == null ? '' : scadenzaInParole(days, 'Non versata');
  // STIMA e CALCOLATO sono stime nostre: la cifra può ancora cambiare.
  const stima = (TAX_SOURCES_PROVVISORIE as string[]).includes(t.source);
  const src = stima ? ` · ${((TAX_SOURCE_LABEL as Record<string, string>)[t.source] ?? 'Stima').toLowerCase()}, la cifra può cambiare` : '';
  const id = t.installmentId ?? t.obligationId ?? `${t.kind}-${t.dueDate}-${idx}`;
  if (!t.amount) return cifraMancanteItem({ id, description: t.description, dueDate: t.dueDate, days });
  return {
    key: `tax:${id}`,
    band: 0,
    kind: 'tax-due',
    icon: 'calendarClock',
    title: t.description,
    sub: `${when}${src}`,
    amount: t.amount,
    dueAt: t.dueDate,
    entity: { type: 'tax', id, path: ADMIN_PATH.calendario },
    primary: { label: 'Apri il calendario', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/** Scadenza fiscale senza cifra: prima di pagarla bisogna scrivere quanto. Entro 7 giorni è critica. */
function cifraMancanteItem(t: { id: string; description: string; dueDate: string | null; days: number | null }): ActionItem {
  const days = t.days;
  return {
    key: `tax:${t.id}`,
    band: days != null && days <= 7 ? 0 : 2,
    kind: 'tax-missing',
    icon: 'pencil',
    title: `Scrivi la cifra: ${t.description}`,
    sub: `${days == null ? 'Senza scadenza' : scadenzaInParole(days, 'Non versata')} · a sistema la cifra è zero`,
    dueAt: t.dueDate,
    entity: { type: 'tax', id: t.id, path: ADMIN_PATH.calendario },
    primary: { label: 'Apri il calendario', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/**
 * Una fattura di un fornitore e il bonifico che la paga, con importo, nome e
 * data che combaciano: basta confermarlo. Si conferma in Banca, dove la prova
 * sta accanto alla riga (confermare un'uscita da qui con un clic solo è il
 * modo in cui si assegna il bonifico sbagliato).
 */
function abbinamentoSicuroItem(a: AbbinamentoSicuroUscita): ActionItem {
  return {
    key: `payable-rata:${a.rataId}`,
    band: 1,
    kind: 'bank-sure',
    icon: 'swap',
    title: `Conferma il pagamento a ${a.controparte}`,
    sub: `In banca c'è il bonifico giusto${a.movimento.settledOn ? `, del ${fmtDate(a.movimento.settledOn)}` : ''}: basta confermarlo in Banca`,
    amount: a.importo,
    dueAt: a.dueDate,
    entity: { type: 'payable-rata', id: a.rataId, path: adminHref('banca') },
    primary: { label: 'Apri Banca', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/**
 * Un bonifico sicuro a un fornitore arrivato da `fatture.toReview` (lato
 * USCITA) senza la sua riga in `daFare.abbinamentiSicuriUscite`: stessa riga
 * di abbinamentoSicuroItem, con i campi della proposta.
 */
function uscitaDaToReview(t: ToReview): AbbinamentoSicuroUscita {
  return {
    rataId: t.paymentId,
    docId: t.invoiceId ?? '',
    docNumero: t.invoiceNumber,
    controparte: t.clientName,
    importo: t.amount,
    dueDate: t.dueDate,
    movimento: { id: t.transaction.id, settledOn: t.transaction.settledOn || null, label: t.transaction.label, counterparty: t.transaction.counterparty },
  };
}

/**
 * Un canone con la scadenza passata e senza la fattura del mese: va emesso
 * (su FattureInCloud). La riga porta al cliente, dove ci sono i suoi
 * documenti. Oltre una settimana di ritardo sale fra le cose da fare subito.
 */
function canoneItem(c: CanoneDaFatturare): ActionItem {
  const days = daysFromToday(c.dueDate);
  return {
    key: `canone:${c.recurringRevenueId}:${c.mese}`,
    band: days != null && days < -7 ? 0 : 1,
    kind: 'canone-da-fatturare',
    icon: 'invoice',
    title: `Fattura il canone di ${meseInParole(c.mese)} di ${c.cliente}`,
    sub: `${scadenzaInParole(days, 'Scaduto')} · la fattura del mese non c'è ancora`,
    amount: c.importo,
    dueAt: c.dueDate,
    entity: { type: 'canone', id: `${c.recurringRevenueId}:${c.mese}`, path: `/clients/${c.clientId}` },
    primary: { label: 'Apri il cliente', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/**
 * Stipendi e TFR che in banca non risultano versati: si verificano in
 * Dipendenti. Senza importo: la stima ha la sua casa in Dipendenti (R2) e non
 * è un pagamento scaduto, è da verificare (SPEC, correzione 7).
 */
function versamentiItem(v: VersamentiDaVerificare): ActionItem {
  const cosa = v.stipendi > 0 && v.tfr > 0 ? 'gli stipendi e i TFR' : v.stipendi > 0 ? 'gli stipendi' : 'i TFR';
  return {
    key: 'dipendenti:versamenti',
    band: 2,
    kind: 'versamenti-da-verificare',
    icon: 'user',
    title: `Verifica ${cosa} non ancora versati (${v.count})`,
    sub: `In banca non c'è il bonifico: ${elenco(v.persone)}`,
    dueAt: null,
    entity: { type: 'dipendenti', id: 'versamenti', path: adminHref('dipendenti') },
    primary: { label: 'Apri Dipendenti', kind: 'apri' },
    canSnooze: true,
    canDone: false,
  };
}

/** Un aggiornamento da Qonto o FattureInCloud non è riuscito, o i dati sono fermi: "Riprova". */
function syncItem(s: Partial<StatoSync> & { provider: 'QONTO' | 'FIC' }): ActionItem {
  const nome = s.nome ?? (s.provider === 'QONTO' ? 'Qonto' : 'FattureInCloud');
  return {
    key: `sync:${s.provider}`,
    band: 1,
    kind: 'sync-failed',
    icon: 'refresh',
    title: s.testoProblema ?? `${nome} non si aggiorna`,
    sub: s.errore ? `Errore: ${s.errore}` : `Senza aggiornamento i numeri di ${s.provider === 'QONTO' ? 'banca' : 'fatture'} restano quelli di prima`,
    dueAt: null,
    entity: { type: 'sync', id: s.provider, path: ADMIN_PATH.calendario },
    primary: { label: 'Riprova', kind: 'riprova' },
    canSnooze: true,
    canDone: false,
    actionData: { provider: s.provider },
  };
}

/**
 * Ordinamento di severità DENTRO la banda (le bande sono già ordinate asc):
 *   band 0 → alert di progetto prima, poi fatture per giorni-ritardo desc
 *            (dueAt più vecchia prima) e importo desc;
 *   band 1-2 → scadenza più vicina prima (senza data in coda), poi importo desc;
 *   band 3 → ordine d'arrivo (l'API consegna già le notifiche createdAt desc).
 */
function compareItems(a: ActionItem, b: ActionItem): number {
  if (a.band !== b.band) return a.band - b.band;
  if (a.band === 3) return 0; // sort stabile: preserva l'ordine della fonte
  if (a.band === 0) {
    const ka = a.kind === 'project-alert' ? 0 : 1;
    const kb = b.kind === 'project-alert' ? 0 : 1;
    if (ka !== kb) return ka - kb;
  }
  const da = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
  const db = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
  if (da !== db) return da - db;
  return (b.amount ?? 0) - (a.amount ?? 0);
}

export function useActionStream(opts: { scope?: 'admin' } = {}): {
  items: ActionItem[];
  counts: ActionStreamCounts;
  bands: ActionBandGroup[];
  isLoading: boolean;
} {
  const scopeAdmin = opts.scope === 'admin';
  const user = useAuthStore((s) => s.user);
  const snoozedMap = useActionStreamStore((s) => s.snoozed);
  const archivedFyi = useActionStreamStore((s) => s.archivedFyi);

  // Gating per modulo: stesso pattern della Sidebar (hasAtLeast READ).
  const canSee = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  };
  const canFatturazione = canSee(ModuleKey.FATTURAZIONE);
  const canDashboard = canSee(ModuleKey.DASHBOARD);
  const canTalent = canSee(ModuleKey.TALENT);
  const canTasks = canSee(ModuleKey.TASKS);
  const canAdmin = canSee(ModuleKey.AMMINISTRAZIONE);
  const canSeeMargin = !!user?.role.canSeeMargin;

  // Fonti dati — stesse queryKey delle viste esistenti: cache condivisa, zero fetch extra.
  const { data: notifData, isLoading: loadingNotif } = useNotifications();
  const { data: invoiceData, isLoading: loadingInv } = useQuery({ ...invoicesQueryOptions, enabled: canFatturazione });
  const { data: quoteData, isLoading: loadingQuotes } = useQuery({ ...quotesQueryOptions, enabled: canFatturazione });

  // Trimestre corrente: una queryKey per trimestre, così il fetch si fa una volta sola.
  const today = new Date();
  const currentQ = Math.floor(today.getMonth() / 3) + 1;
  const currentY = today.getFullYear();
  const { data: overview, isLoading: loadingDash } = useQuery({
    queryKey: ['dashboard', currentQ, currentY],
    queryFn: () => api.get<OverviewLite>(`/dashboard/overview?q=${currentQ}&y=${currentY}`),
    enabled: canDashboard,
  });

  // Task: il riepilogo "mine" (stessa query del badge Sidebar); staleTime e
  // refetchInterval vivono nelle query options → nessun fetch a raffica.
  const { data: taskSummary, isLoading: loadingTasks } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canTasks });

  // Amministrazione: overview (tasse, da controllare) + rate aperte a 30 giorni
  // (stesse query della pagina Oggi e di /admin: cache condivisa). Se l'API
  // non risponde, lo stream vive lo stesso con le altre fonti.
  const { data: adminOverview, isLoading: loadingAdmin } = useQuery({ ...adminOverviewQuery, enabled: canAdmin, retry: 1 });
  const { data: rateData, isLoading: loadingRate } = useQuery({ ...upcomingRateQuery(), enabled: canAdmin, retry: 1 });

  const isLoading = loadingNotif
    || (canFatturazione && (loadingInv || loadingQuotes))
    || (canDashboard && loadingDash)
    || (canTasks && loadingTasks)
    || (canAdmin && (loadingAdmin || loadingRate));

  const composed = useMemo(() => {
    const now = Date.now();
    // Fine di domani = tetto della banda 1 ("entro domani")
    const tomorrowEnd = new Date();
    tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
    tomorrowEnd.setHours(23, 59, 59, 999);
    const band1Cutoff = tomorrowEnd.getTime();
    const weekCutoff = now + 7 * DAY_MS;

    const collected: ActionItem[] = [];

    // ── Amministrazione: rate (sostituiscono le fatture dello scadenziario), tasse, da controllare ──
    const useAdminRates = canAdmin && !!rateData;
    if (canAdmin) {
      const extra = lotto4(adminOverview);
      // `toReview` porta anche i pagamenti sicuri ai fornitori (lato USCITA).
      // adminApi.normalizeReview non conserva `lato`: si riconoscono anche
      // dalla lista delle uscite sicure. Un'uscita non si conferma con un clic
      // (serve la prova accanto): diventa la riga "Conferma il pagamento a ...".
      const usciteSicure = new Map<string, AbbinamentoSicuroUscita>();
      for (const a of extra.daFare?.abbinamentiSicuriUscite ?? []) usciteSicure.set(a.rataId, a);
      const reviewByPayment = new Map<string, ToReview>();
      for (const t of adminOverview?.fatture.toReview ?? []) {
        const uscita = (t as ToReview & { lato?: string }).lato === 'USCITA' || usciteSicure.has(t.paymentId);
        if (uscita) { if (!usciteSicure.has(t.paymentId)) usciteSicure.set(t.paymentId, uscitaDaToReview(t)); }
        else reviewByPayment.set(t.paymentId, t);
      }
      for (const a of usciteSicure.values()) collected.push(abbinamentoSicuroItem(a));
      for (const c of extra.daFare?.canoniDaFatturare ?? []) collected.push(canoneItem(c));
      const seenRate = new Set<string>();
      for (const r of rateData?.items ?? []) {
        if (r.status !== 'DA_INCASSARE') continue;
        const days = daysFromToday(r.dueDate);
        if (days == null) continue;
        const band: 0 | 1 | 2 | null = days < 0 ? 0 : days <= 1 ? 1 : days <= 7 ? 2 : null;
        if (band == null) continue; // oltre 7 giorni: vive nella card "Prossime scadenze", non nello stream
        seenRate.add(r.id);
        collected.push(rataItem(r, band, reviewByPayment.get(r.id)));
      }
      for (const t of reviewByPayment.values()) {
        if (!seenRate.has(t.paymentId)) collected.push(toReviewItem(t));
      }
      (adminOverview?.tasse.next ?? []).forEach((t, i) => {
        const days = daysFromToday(t.dueDate);
        if (days != null && days <= 7) collected.push(taxItem(t, i));
      });

      // Solo nella Situazione: le cose da fare che non sono né rate né tasse in scadenza.
      if (scopeAdmin) {
        const v = extra.daFare?.versamentiDaVerificare;
        if (v && v.count > 0) collected.push(versamentiItem(v));
        // Le cifre mancanti entro 30 giorni (entro 7 le ha già messe taxItem, con la stessa chiave).
        for (const c of extra.daFare?.cifreMancanti ?? ([] as CifraMancante[])) {
          collected.push(cifraMancanteItem({ id: c.installmentId, description: c.description, dueDate: c.dueDate, days: daysFromToday(c.dueDate) }));
        }
        for (const p of ['QONTO', 'FIC'] as const) {
          const s = p === 'QONTO' ? extra.sync?.qonto : extra.sync?.fic;
          if (s?.problema) collected.push(syncItem({ ...s, provider: p }));
        }
      }
    }

    // ── Contabilità: scadenziario → fatture e preventivi azionabili ──
    if (canFatturazione && invoiceData && quoteData) {
      const rows = toUnifiedRows(quoteData.items ?? [], invoiceData.items ?? []);
      for (const r of computeUpcoming(rows)) {
        const due = r.secondaryDate ? new Date(r.secondaryDate).getTime() : null;
        if (r.kind === 'invoice') {
          if (useAdminRates) continue; // le rate dell'Amministrazione coprono già queste fatture
          if (due != null && due < now) collected.push(invoiceOverdueItem(r, due, now));
          else if (due != null && due <= band1Cutoff) collected.push(invoiceDueItem(r, due, now, 1));
          else if (due != null && due <= weekCutoff) collected.push(invoiceDueItem(r, due, now, 2));
          // oltre 7 giorni (o senza scadenza): fuori dallo stream "Oggi", vive nello Scadenziario
        } else {
          // preventivi INVIATO/VISUALIZZATO (già filtrati da computeUpcoming)
          const near = due != null && due <= weekCutoff;
          collected.push(quotePendingItem(r, near ? 1 : 2, due, now));
        }
      }
      // Fatture già marcate SCADUTA: escluse da computeUpcoming (che considera
      // solo EMESSA/INVIATA) ma sono comunque incassi in ritardo → banda 0.
      for (const r of rows) {
        if (useAdminRates) break;
        if (r.kind === 'invoice' && r.status === 'SCADUTA') {
          const due = r.secondaryDate ? new Date(r.secondaryDate).getTime() : null;
          collected.push(invoiceOverdueItem(r, due, now));
        }
      }
    }

    // ── Task miei: in ritardo → Critico, scadono oggi → Oggi (link al dettaglio) ──
    if (canTasks && taskSummary) {
      for (const t of taskSummary.overdueItems ?? []) collected.push(taskActionItem(t, 0));
      for (const t of taskSummary.dueTodayItems ?? []) collected.push(taskActionItem(t, 1));
    }

    // ── Alert di progetto (fonte: ['notifications'], come campanella e Dashboard).
    // Dedup con gli alert dell'overview: sono gli STESSI record ProjectAlert,
    // quindi si consuma solo questa fonte — nessun doppione possibile.
    for (const a of notifData?.projectAlerts ?? []) {
      const marginRelated = /margin/i.test(a.message); // copre "margine" e "margin"
      if (marginRelated && !canSeeMargin) continue;    // gating margine per ruolo
      const critical = a.type === 'ALERT' || marginRelated || /risk|rischio/i.test(a.message);
      collected.push({
        key: a.project?.id ? `project:${a.project.id}` : `project-alert:${a.id}`,
        band: critical ? 0 : 1,
        kind: 'project-alert',
        icon: 'alert',
        title: a.message,
        sub: a.project ? `${a.project.code} · ${a.project.name}` : 'Alert di progetto',
        dueAt: null,
        entity: a.project?.id
          ? { type: 'project', id: a.project.id, path: `/projects/${a.project.id}` }
          : { type: 'project', id: a.id, path: '/' },
        primary: { label: 'Segna risolto', kind: 'risolvi' },
        canSnooze: true,
        canDone: true, // done = Risolvi (chiude l'alert per tutti)
        actionData: { alertId: a.id },
      });
    }

    // ── Item aggregati dall'overview (derivati: si aprono, non si "completano") ──
    const draftCount = canDashboard ? overview?.kpis.draftProjects ?? 0 : 0;
    if (draftCount > 0 && canFatturazione) {
      collected.push({
        key: 'project:drafts',
        band: 2,
        kind: 'draft-project',
        icon: 'file',
        title: `${draftCount} progett${draftCount === 1 ? 'o' : 'i'} in preventivo`,
        sub: 'Bozze da trasformare in proposta firmabile',
        dueAt: null,
        entity: { type: 'project', id: 'drafts', path: adminLinks.preventivi() },
        primary: { label: 'Apri i preventivi', kind: 'apri' },
        canSnooze: true,
        canDone: false,
      });
    }
    const candCount = canDashboard ? overview?.kpis.pipelineCandidates ?? 0 : 0;
    if (candCount > 0 && canTalent) {
      collected.push({
        key: 'candidate:pipeline',
        band: 2,
        kind: 'candidate-ready',
        icon: 'talent',
        title: `${candCount} candidat${candCount === 1 ? 'o' : 'i'} in pipeline`,
        sub: 'Pronti per screening o placement',
        dueAt: null,
        entity: { type: 'candidate', id: 'pipeline', path: '/talent' },
        primary: { label: 'Apri i candidati', kind: 'apri' },
        canSnooze: true,
        canDone: false,
      });
    }

    // ── Notifiche personali non lette → FYI (band 3) ──
    for (const n of notifData?.personal ?? []) {
      if (n.readAt) continue; // il letto è già "gestito"
      const ent = entityFromLink(n.link);
      collected.push({
        key: ent ? `${ent.type}:${ent.id}` : `notification:${n.id}`,
        band: 3,
        kind: 'notification',
        icon: NOTIF_ICON[n.icon] ?? 'bell',
        title: n.message,
        sub: `Avviso del ${fmtDateShort(n.createdAt)}`,
        dueAt: null,
        entity: ent ?? { type: 'notification', id: n.id, path: n.link ?? '/' },
        primary: n.link ? { label: 'Apri', kind: 'apri' } : undefined,
        canSnooze: false, // le FYI si archiviano, non si rimandano
        canDone: true,    // done = segna come letta + archiveFyi
        actionData: { notifId: n.id },
      });
    }

    // ── Ordina, deduplica, applica i filtri locali ──
    const sorted = [...collected].sort(compareItems);
    // Dedupe per chiave entità: la prima occorrenza vince = banda più bassa (più urgente)
    const byKey = new Map<string, ActionItem>();
    for (const it of sorted) if (!byKey.has(it.key)) byKey.set(it.key, it);

    const archived = new Set(archivedFyi);
    const visible: ActionItem[] = [];
    let snoozedCount = 0;
    for (const it of byKey.values()) {
      // Situazione: solo le voci dell'Amministrazione (contate a parte anche nei posticipati).
      if (scopeAdmin && !ADMIN_KINDS.has(it.kind)) continue;
      // FYI archiviate: fuori per sempre (né visibili né contate)
      if (it.kind === 'notification' && archived.has(it.key)) continue;
      // Snooze attivo: nascosto ma contato; se la scadenza è passata, auto-scade
      const until = snoozedMap[it.key];
      if (until && new Date(until).getTime() > now) snoozedCount += 1;
      else visible.push(it);
    }

    const counts: ActionStreamCounts = {
      critical: visible.filter((i) => i.band === 0).length,
      today: visible.filter((i) => i.band === 1).length,
      week: visible.filter((i) => i.band === 2).length,
      later: visible.filter((i) => i.band === 3).length,
      snoozed: snoozedCount,
    };
    const bands: ActionBandGroup[] = ([0, 1, 2, 3] as const).map((band) => ({
      band,
      label: BAND_LABEL[band],
      items: visible.filter((i) => i.band === band),
    }));

    return { items: visible, counts, bands };
  }, [invoiceData, quoteData, notifData, overview, taskSummary, adminOverview, rateData, snoozedMap, archivedFyi, canFatturazione, canDashboard, canTalent, canTasks, canAdmin, canSeeMargin, scopeAdmin]);

  return { ...composed, isLoading };
}
